import type { ResourcePrice } from "@@/shared/acquire/resource-price/resource-price";
import { isResourcePriceByTimeSteps } from "@@/shared/acquire/resource-price/resource-price-by-time-step";
import { groupBy } from "@@/utilities/group-by";
import { sortBy } from "@@/utilities/sort";
import { unique } from "@@/utilities/unique";
import {
    isQuantityPickerBookableOptionValue,
    type QuantityPickerBookableOptionValue,
} from "../resource-reference-options/quantity-picker-bookable-option-value";
import type { ResourceReservation } from "./resource-reservation";
import type { ResourceReservationRequest } from "./resource-reservation-request";

//Consolidate/sum the same picked option over different resources reservations
//is used because we book individual resources for ex cars. So the booking will be 1 car, 1 car instead of 2cars.
//This function consolidates the same ResourceReference bookedOption to the same quantity.
//If the ResourceReference has scheduling (Motorstadion) it will only take the first time and consolidate it.
//Else you will get duplicate quantity. Ex car 1 is scheduled to be booked two times during the booking-window
//for 15min, between 12:00-12:15 and then at 12:30-12:45, this will be 2 reservations for the same car.
//So we need to only use the first time as the time we consolidate for

const consolidateResourceRequestsOption = (
    resourceReservationRequests: (
        | ResourceReservationRequest
        | ResourceReservation
    )[],
) => {
    if (resourceReservationRequests.length === 0) return [];

    // Group by time to get only one
    const groupByTimeRange = groupBy(
        resourceReservationRequests,
        t => t.timeRange._id,
    );
    const resourceReservationRequestsSortedByTimeRange =
        resourceReservationRequests.sort(
            sortBy(item => item.timeRange.start.unix),
        );
    const firstReservationResourceRequests =
        groupByTimeRange.get(
            resourceReservationRequestsSortedByTimeRange?.[0]?.timeRange._id,
        ) ?? resourceReservationRequestsSortedByTimeRange;

    const result = firstReservationResourceRequests
        .flatMap(r => r.optionValues)
        .filter(isQuantityPickerBookableOptionValue)
        .reduce((aggregate, current) => {
            if (!isQuantityPickerBookableOptionValue(current)) return aggregate;
            const exists = aggregate.find(
                optionValue => optionValue._id === current._id,
            );

            if (exists) {
                const newQuantity =
                    exists.quantity.value + current.quantity.value;

                return [
                    {
                        ...exists,
                        resourcePrice: exists.resourcePrice,
                        // resourcePrice:
                        // exists.resourcePrice.price.amountIncludingVat >
                        // current.resourcePrice.price.amountIncludingVat
                        //     ? exists.resourcePrice
                        //     : current.resourcePrice,

                        quantity: {
                            ...exists.quantity,
                            value: newQuantity,
                        },
                    },
                    ...aggregate.filter(f => f._id !== current._id),
                ];
            }

            return [current, ...aggregate];
        }, [] as QuantityPickerBookableOptionValue[]);

    // console.log(
    //     "⚡️ consolidateResourceRequestsOption " +
    //         result?.map(item =>
    //             typeof item.meta.title === "string"
    //                 ? item.meta.title
    //                 : // eslint-disable-next-line @typescript-eslint/no-explicit-any
    //                   (item.meta.title as any)?.sv || ""
    //         ) ?? "",
    //     {
    //         groupByTimeRange,
    //         resourceReservationRequestsSortedByTimeRange,
    //         firstReservationResourceRequests,
    //         result,
    //     }
    // );

    return result;
};

type MissingOptionValue = {
    optionValue: QuantityPickerBookableOptionValue;
    missingQuantity: number;
};

const missingOptionValueFactory = (
    params: MissingOptionValue,
): MissingOptionValue => {
    return {
        ...params,
    };
};

const consolidateResourceRequestsOptionPerResourceReference = (
    requests: (ResourceReservationRequest | ResourceReservation)[],
) => {
    if (requests.length === 0) return [];

    const refs = unique(
        requests.flatMap(r => r.optionValues).map(o => o.resourceReferenceId),
    );

    const grouped = refs.flatMap(r =>
        consolidateResourceRequestsOption(
            requests.filter(f =>
                f.optionValues.find(f => f.resourceReferenceId === r),
            ),
        ),
    );
    return grouped;
};

const isResourcePriceFree = (resourcePrice: ResourcePrice) => {
    if (isResourcePriceByTimeSteps(resourcePrice)) {
        return resourcePrice.steps.every(
            step => step.price.amountIncludingVat === 0,
        );
    }
    return resourcePrice.price.amountIncludingVat === 0;
};

const resourceUtils = {
    isResourcePriceFree,
};

export {
    consolidateResourceRequestsOption,
    consolidateResourceRequestsOptionPerResourceReference,
    missingOptionValueFactory,
    resourceUtils,
};

export type { MissingOptionValue };
